<template>
  <div class="px-4">
    <div class="text-gray-800 text-sm dark:text-gray-100">
      <div v-if="!hasAttendees" class="text-center">
        <p class="text-gray-600 m-4 dark:text-gray-300">
          {{ $t('components.reservation.edit.attendees.noAttendees') }}
        </p>

        <!-- Use dropdown if reservation belongs to a course, otherwise use regular button -->
        <o-dropdown
          v-if="isCourseClass"
          ref="addCustomerDropdown"
          placement="bottom-start"
          :auto-hide="true"
          :portal="panel.portal"
        >
          <o-button icon="customerAdd" variant="primary">
            {{ $t('components.reservation.edit.attendees.addACustomer') }}
          </o-button>

          <template #content>
            <div class="py-1">
              <o-dropdown-item
                name="Add to this class only"
                description="Drop-in customer for this appointment only"
                icon="customerAdd"
                @click="handleAddCustomer"
              />
              <o-dropdown-item
                v-if="canEnrolCourses"
                name="Enrol in course"
                description="Enrol customer in the full course"
                icon="course"
                @click="handleEnrolInCourse"
              />
            </div>
          </template>
        </o-dropdown>
        <o-button
          v-else
          icon="customerAdd"
          variant="primary"
          @click="handleAddCustomer"
        >
          {{ $t('components.reservation.edit.attendees.addACustomer') }}
        </o-button>
      </div>
      <template v-if="hasAttendees">
        <div class="w-full flex items-center justify-between mb-4">
          <o-field-group class="max-w-60">
            <text-field v-model="filter" :placeholder="$t('components.reservation.edit.attendees.search')" trailing="search" />
          </o-field-group>

          <!-- Use dropdown if reservation belongs to a course, otherwise use regular button -->
          <o-dropdown
            v-if="isCourseClass"
            ref="addCustomerDropdown"
            placement="bottom-start"
            :auto-hide="true"
            :portal="panel.portal"
          >
            <o-button icon="customerAdd" variant="primary">
              {{ $t('components.reservation.edit.attendees.addCustomer') }}
            </o-button>

            <template #content>
              <div class="py-1">
                <o-dropdown-item
                  name="Add to this class only"
                  description="Drop-in customer for this appointment only"
                  icon="customerAdd"
                  @click="handleAddCustomer"
                />
                <o-dropdown-item
                  v-if="canEnrolCourses"
                  name="Enrol in course"
                  description="Enrol customer in the full course"
                  icon="course"
                  @click="handleEnrolInCourse"
                />
              </div>
            </template>
          </o-dropdown>
          <o-button
            v-else
            icon="customerAdd"
            variant="primary"
            @click="handleAddCustomer"
          >
            {{ $t('components.reservation.edit.attendees.addCustomer') }}
          </o-button>
        </div>
        <o-table
          class="border rounded bg-white dark:bg-gray-900"
          :headers="[
            {text: $t('general.customer'), sortKey: 'customer.displayName'},
            $t('general.status'),
            {text: $t('general.price'), sortKey: 'price'},
            $t('general.notes'),
            $t('general.actions'),
          ]"
          :data="groupCustomers"
          :limit="20"
          :tbody-class="{
            tfoot: '',
            tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
            td: 'py-3 px-4',
            stripe: 'bg-gray-50/50 dark:bg-gray-900'
          }"
          :thead-class="{
            thead: 'border-b',
            tr: 'border-blue-700',
            th: 'font-medium p-4 pb-3 bg-gray-50 text-gray-700 text-shadow sticky top-0 dark:bg-gray-900 dark:text-gray-200',
            content: 'flex item-center'
          }"
          striped
        >
          <template #row="props">
            <attendee
              :props="props"
              :attendee="props.row"
              :reservation="reservation"
              @update="$emit('update')"
              @update:loading="$emit('update:loading', $event)"
            />
          </template>
        </o-table>
      </template>
    </div>

    <!-- Keep the existing add-attendee-action for non-course reservations -->
    <add-attendee-action
      ref="addAttendeeAction"
      :portal="panel.portal"
      :reservation="reservation"
      @update:reservation="$emit('update:reservation', $event)"
      @update:loading="$emit('update:loading', $event)"
      @update="$emit('update')"
      @success="panel.close()"
    />
  </div>
</template>

<script setup lang="ts">
import Fuse from 'fuse.js'

import Attendee from './Attendee.vue'
import AddAttendeeAction from '@/components/reservation/actions/AttendeeAdd.vue'

import {
  OTable,
  ODropdown,
  ODropdownItem,
} from '@/components/elements'

import {
  TextField,
} from '@/components/fields'

const { $api, $panel, $gates, $grants } = useNuxtApp()
const panel = inject<any>('panel')

interface Props {
  reservation?: Record<string, any> | null
}

interface Emits {
  (e: 'update'): void
  (e: 'update:reservation', value: any): void
  (e: 'update:loading', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  reservation: null,
})

const emit = defineEmits<Emits>()

const filter = ref('')
const addAttendeeAction = ref<any>()
const addCustomerDropdown = ref<any>()

const groupCustomers = computed(() => {
  const filterVal = filter.value
  const customers = props.reservation!.groupCustomers

  if (!filterVal) {
    return customers
  }

  const fuse = new Fuse(customers, {
    keys: [
      'customer.firstName',
      'customer.lastName',
      'customer.email',
    ],
  })

  const results = fuse.search(filterVal)
  return results.map(({ item }: any) => item)
})

const hasAttendees = computed(() => !!props.reservation!.groupCustomers.length)

const isCourseClass = computed(() => {
  return props.reservation!.courseRunReservations?.length > 0
})

const courseRun = computed(() => {
  return props.reservation!.courseRunReservations?.[0]?.courseRun
})

const canEnrolCourses = computed(() => $gates.get('courses') && $grants.all({ customerCourse: ['create'] }))

async function handleAddCustomer () {
  addCustomerDropdown.value?.close()
  await nextTick()
  addAttendeeAction.value?.open()
}

function handleEnrolInCourse () {
  if (canEnrolCourses.value && courseRun.value) {
    const path = '/customer/course/add'
    const options = {
      props: {
        presets: { courseRun: courseRun.value },
      },
      events: {
        success: handleEnrolSuccess,
      },
    }
    $panel.create(path, options)
  }
}

async function handleEnrolSuccess () {
  emit('update:loading', true)

  try {
    const response = await $api.reservation.getWithDate(
      String(props.reservation!.idReservation),
      props.reservation!.date.integer,
    )
    emit('update:reservation', response.data)
    emit('update')
  } finally {
    emit('update:loading', false)
  }
}
</script>
